import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../../../interceptors";
import { baseUrl } from "../../../config/config";
import EmptyState from "../../../components/shared/EmptyState";
import LoadingState from "../../../components/shared/LoadingState";
import ErrorState from "../../../components/shared/ErrorState";
import { toast } from "../../../components/shared/toast";

const fmt = (iso) => (iso ? new Date(iso).toLocaleString() : "—");

export default function ApprovalChainList() {
  const nav = useNavigate();
  const [chains, setChains] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [errorStatus, setErrorStatus] = useState(null);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    setErrorStatus(null);
    try {
      const r = await axiosInstance.get(`${baseUrl}/job-posting/approval-chains`);
      setChains(Array.isArray(r.data) ? r.data : []);
    } catch (e) {
      setError(e?.message || "Failed to load");
      setErrorStatus(e?.status || e?.response?.status || 500);
    } finally {
      setLoading(false);
    }
  };

  const archive = async (c) => {
    if (!window.confirm(`Archive chain "${c.name}"? It won't apply to new offers.`)) return;
    setBusyId(c.id);
    try {
      await axiosInstance.delete(`${baseUrl}/job-posting/approval-chains/${c.id}`);
      toast.success(`Archived "${c.name}"`);
      await load();
    } catch (e) {
      toast.error("Archive failed", { description: e?.message });
    } finally { setBusyId(null); }
  };

  useEffect(() => { load(); }, []);

  return (
    <div className="page-enter">
      <div className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold text-vf-base">Approval chains</h1>
          <p className="text-sm text-vf-muted mt-1">
            Multi-step approval workflows applied to offers before they reach the candidate.
          </p>
        </div>
        <button onClick={() => nav("/admin/approval-chains/new")}
                className="px-3 py-1.5 rounded bg-vf-primary text-white text-sm font-medium">
          + New chain
        </button>
      </div>

      {error ? (
        <ErrorState status={errorStatus} description={errorStatus === 403 ? undefined : error} retry={load} />
      ) : (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading && <div className="p-4"><LoadingState variant="skeleton" shape="row" count={3} /></div>}
        {!loading && chains.length === 0 && (
          <EmptyState
            title="No approval chains yet"
            description="Define a multi-step approval workflow that applies to offers before they reach the candidate."
            primaryAction={{ label: "+ New chain", to: "/admin/approval-chains/new" }}
          />
        )}
        {!loading && chains.length > 0 && (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th>Name</Th><Th>Applies to</Th><Th>Default</Th><Th>Updated</Th><Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody>
              {chains.map((c) => (
                <tr key={c.id} className="border-t border-slate-100">
                  <td className="px-3 py-2 font-medium">{c.name}</td>
                  <td className="px-3 py-2 text-xs">{c.appliesTo}</td>
                  <td className="px-3 py-2">
                    {c.isDefault && (
                      <span className="px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-700 text-[11px] font-medium">Default</span>
                    )}
                  </td>
                  <td className="px-3 py-2 text-xs text-vf-muted">{fmt(c.createdAt)}</td>
                  <td className="px-3 py-2 text-right space-x-1">
                    <button onClick={() => nav(`/admin/approval-chains/${c.id}`)}
                            className="px-2 py-1 rounded border border-slate-300 text-xs">Edit</button>
                    <button disabled={busyId === c.id} onClick={() => archive(c)}
                            className="px-2 py-1 rounded border border-red-300 text-red-700 text-xs disabled:opacity-50">
                      Archive
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
      )}
    </div>
  );
}

const Th = ({ children, className = "" }) => (
  <th className={`px-3 py-2 text-left text-[11px] uppercase tracking-wide text-vf-muted font-semibold ${className}`}>
    {children}
  </th>
);
